import React, { memo, useEffect, useRef, useState } from 'react';
import { BackSvg, Poppins, Spacer, TableSkeleton } from 'src/common';
import _, { isEqual } from 'lodash';
import Button from 'src/components/form/Button';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import { TableIds } from 'src/api/types/misc';
import GenericTable, { TableSheetControlLibrary } from 'src/components/GenericTable';
import { isQueryLoading } from 'src/graphql/client';
import { ControlFrameworkDetailsQuery } from 'src/graphql/_generated';
import { useControlFrameworkDetailsQuery } from 'src/graphql/_generated';
import { useControlColumns } from '../useControlColumns';
import { useFrameworkColumns } from '../useFrameworkColumns';
import styled from 'styled-components';

const BackWrap = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
`;

interface FromExistingProps {
  setChecked: (a: string | null) => void;
  onSubmit: () => void;
  isSubmitting?: boolean;
  existingFrameworkControlIds: string[];
  checked: string | null;
  selectedFrameworks?: string[];
  setSelectedFrameworks?: (a: string[]) => void;
  initialSelectedFrameworks?: string[];
}

export const FromExisting: React.FC<FromExistingProps> = memo(
  ({
    onSubmit,
    isSubmitting,
    existingFrameworkControlIds,
    setChecked: setCheckedParent,
    checked,
    selectedFrameworks,
    initialSelectedFrameworks,
    setSelectedFrameworks,
  }) => {
    const { data, loading, networkStatus } = useControlFrameworkDetailsQuery();
    const isLoading = isQueryLoading({ data, loading, networkStatus });

    const [searchValue, setSearchValue] = useState('');
    const [showAllControlsFromSearch, setShowAllControlsFromSearch] = useState(false);
    const searchInputRef = useRef<HTMLInputElement>(null);

    const frameworks = data?.controlFrameworks || [];

    // When search is cleared and we're in the "show all from search" mode, go back to frameworks view
    useEffect(() => {
      if (showAllControlsFromSearch && !searchValue) {
        setShowAllControlsFromSearch(false);
        setSelectedFrameworks?.([]);
      }
    }, [searchValue, showAllControlsFromSearch, setSelectedFrameworks]);

    // Focus input when switching to controls view from search
    useEffect(() => {
      if (showAllControlsFromSearch && searchInputRef.current) {
        // Small delay to ensure the input is rendered
        setTimeout(() => {
          searchInputRef.current?.focus();
        }, 50);
      }
    }, [showAllControlsFromSearch]);

    const handleFrameworksSearchChange = (value: string) => {
      setSearchValue(value);
      // If user types anything in frameworks search, switch to controls view with all frameworks
      if (value && !showAllControlsFromSearch) {
        setShowAllControlsFromSearch(true);
        setSelectedFrameworks?.([]);
      }
    };

    const handleControlsSearchChange = (value: string) => {
      setSearchValue(value);
    };

    const activeFrameworks =
      selectedFrameworks?.length || initialSelectedFrameworks?.length || showAllControlsFromSearch
        ? frameworks.filter((framework) => {
            if (showAllControlsFromSearch) {
              return true;
            }
            const selectedIds = [...(selectedFrameworks || []), ...(initialSelectedFrameworks || [])];
            return selectedIds.includes(framework.shortname || '');
          })
        : frameworks;

    const controlLibs = _.chain(activeFrameworks)
      .flatMap('frameworkControls')
      .orderBy(['control_identifier'])
      .value() as ControlFrameworkDetailsQuery['controlFrameworks'][number]['frameworkControls'];

    const { columns: columnsControls, GTColumnSelectAnchorExported: GTColumnSelectAnchorExportedControls } =
      useControlColumns({
        data: controlLibs,
        isCheckerEnabled: true,
        tableId: TableIds.suggestControls,
        checked,
        setChecked: setCheckedParent,
      });

    const { columns: columnsFrameworks, GTColumnSelectAnchorExported: GTColumnSelectAnchorExportedFrameworks } =
      useFrameworkColumns({
        data: frameworks,
        tableId: TableIds.suggestFrameworks,
        onFrameworkSelect: (framework) => {
          setSelectedFrameworks?.([framework.shortname || '']);
        },
      });

    const showControlsView =
      initialSelectedFrameworks?.length || selectedFrameworks?.length || showAllControlsFromSearch;

    return (
      <>
        {!isLoading ? (
          <>
            {!showControlsView ? (
              <>
                <GenericTable
                  tableId={TableIds.suggestFrameworks}
                  data={frameworks}
                  columns={columnsFrameworks}
                  GTColumnSelectAnchorExported={GTColumnSelectAnchorExportedFrameworks}
                  searchable
                  itemHeight={50}
                  externalSearchValue={searchValue}
                  onSearchChange={handleFrameworksSearchChange}
                />
              </>
            ) : (
              <>
                {!initialSelectedFrameworks?.length && !showAllControlsFromSearch && (
                  <>
                    <BackWrap
                      onClick={() => {
                        setSelectedFrameworks?.([]);
                        setSearchValue('');
                        setShowAllControlsFromSearch(false);
                      }}
                    >
                      <BackSvg />
                      <Poppins weight={500} px={18}>
                        Back
                      </Poppins>
                    </BackWrap>
                    <Spacer $px={20} />
                    <Poppins weight={600} px={20}>
                      {selectedFrameworks?.[0]}
                    </Poppins>
                    <Spacer $px={20} />
                  </>
                )}
                {!initialSelectedFrameworks?.length && showAllControlsFromSearch && (
                  <>
                    <BackWrap
                      onClick={() => {
                        setSelectedFrameworks?.([]);
                        setSearchValue('');
                        setShowAllControlsFromSearch(false);
                      }}
                    >
                      <BackSvg />
                      <Poppins weight={500} px={18}>
                        Back
                      </Poppins>
                    </BackWrap>
                    <Spacer $px={20} />
                  </>
                )}
                <GenericTable
                  tableId={TableIds.suggestControls}
                  data={controlLibs}
                  columns={columnsControls}
                  GTColumnSelectAnchorExported={GTColumnSelectAnchorExportedControls}
                  expandContent={(row) => <TableSheetControlLibrary data={row} />}
                  searchable
                  itemHeight={50}
                  rowDisabled={(row) => existingFrameworkControlIds.includes(row.id)}
                  externalSearchValue={searchValue}
                  onSearchChange={handleControlsSearchChange}
                  searchInputRef={searchInputRef}
                />
                <Spacer $px={30} />
                <Button
                  disabled={isSubmitting || !checked}
                  onClick={() => {
                    onSubmit();
                    mpEvent(MPEvents.ButtonClick, {
                      button: 'Link',
                      modal: 'Suggest frameworks modal',
                      tags: ['CONTROL', 'RMP', 'FRAMEWORK'],
                    });
                  }}
                  primary
                  css="width: 100%;"
                  data-cy="submit-btn"
                >
                  LINK FRAMEWORK TO CONTROL
                </Button>
              </>
            )}
          </>
        ) : (
          <TableSkeleton />
        )}
      </>
    );
  },
  isEqual,
);
